import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Transition,
} from '@headlessui/react';
import {
  Fragment,
  useRef,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  type ReactElement,
} from 'react';

import { Input, InputErrorMsg, Highlight, Icon } from '@/lib';
import { useFuse, useBoxPosition, useBoxSize, mergeRefs } from '@lib/hooks';
import { cn, cva, type VariantProps } from '@lib/utils';

// ----------------------------------------------------------------------

type DataItem = Record<string, unknown>;

interface ClassNames {
  root?: string;
  input?: string;
}

/** State passed to renderOption */
export interface OptionRenderState {
  selected: boolean;
  active: boolean;
}

/** Props for custom option rendering */
export type RenderOptionFn<TValue> = (item: TValue, state: OptionRenderState) => ReactNode;

// CVA variants for dropdown options styling
const optionVariants = cva(
  [
    // Layout
    'relative cursor-pointer px-4 py-2 select-none',
    // Interaction
    'outline-hidden transition-colors',
  ],
  {
    variants: {
      state: {
        default: 'text-fg',
        active: 'bg-ui-hover text-fg',
        selected: 'bg-primary text-white',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

// CVA variants for multiple selection container
const multipleContainerVariants = cva(
  [
    // Layout
    'relative w-full overflow-hidden rounded-lg border',
    // Interaction
    'cursor-default text-start outline-hidden transition-colors focus:outline-hidden',
  ],
  {
    variants: {
      state: {
        default: ['border-line', 'hover:border-line-hover', 'focus-within:border-primary!'],
        error: 'border-error',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

// CVA variants for dropdown panel
const dropdownVariants = cva([
  // Layout
  'absolute z-10 max-h-60 overflow-x-hidden overflow-y-auto rounded-lg py-1',
  // Visual
  'border border-line bg-canvas shadow-lg shadow-black/5',
  // Position
  'left-(--left-anchor)!',
  // Focus
  'outline-hidden focus-visible:outline-hidden',
]);

type BaseComboboxProps<TValue> = {
  data: TValue[];
  placeholder?: string;
  label?: ReactNode;
  displayField?: string;
  error?: boolean | string;
  inputProps?: Record<string, unknown>;
  rootProps?: Record<string, unknown>;
  className?: string;
  classNames?: ClassNames;
  searchFields?: string[];
  highlight?: boolean;
  disabled?: boolean;
  name?: string;
  form?: string;
  invalid?: boolean;
  by?: keyof TValue;
  /** Custom render function for dropdown options */
  renderOption?: RenderOptionFn<TValue>;
};

type SingleComboboxProps<TValue> = BaseComboboxProps<TValue> & {
  multiple?: false;
  value?: TValue | null;
  onChange?: ((value: TValue) => void) | ((value: TValue | null) => void);
  defaultValue?: TValue | null;
  /** Custom render function for input prefix (single mode only) */
  renderInputPrefix?: (value: TValue | null) => ReactNode;
};

type MultipleComboboxProps<TValue> = BaseComboboxProps<TValue> & {
  multiple: true;
  value?: TValue[];
  onChange?: ((value: TValue[]) => void) | Dispatch<SetStateAction<TValue[]>>;
  defaultValue?: TValue[];
  /** Custom render function for selected items (multiple mode only) */
  renderSelected?: (selected: TValue[], onRemove: (item: TValue) => void) => ReactNode;
};

export type AutoCompleteProps<TValue = DataItem> =
  | SingleComboboxProps<TValue>
  | MultipleComboboxProps<TValue>;

export type AutoCompleteOption = DataItem;

// Function overloads for proper type inference
function CustomCombobox<TValue = DataItem>(props: SingleComboboxProps<TValue>): ReactElement;
function CustomCombobox<TValue = DataItem>(props: MultipleComboboxProps<TValue>): ReactElement;

function CustomCombobox<TValue = DataItem>(props: AutoCompleteProps<TValue>): ReactElement {
  const {
    data,
    multiple,
    placeholder,
    label,
    error,
    displayField = 'label',
    searchFields = [],
    highlight,
    inputProps,
    rootProps,
    classNames,
    value,
    onChange,
    defaultValue,
    disabled,
    name,
    form,
    by,
    renderOption,
    ...headlessProps
  } = props;

  // Extract mode-specific render props
  const renderSelected = multiple
    ? (props as MultipleComboboxProps<TValue>).renderSelected
    : undefined;
  const renderInputPrefix = !multiple
    ? (props as SingleComboboxProps<TValue>).renderInputPrefix
    : undefined;

  const {
    result: filteredData,
    query,
    setQuery,
  } = useFuse(data as DataItem[], {
    keys: searchFields,
    threshold: 0.2,
    matchAllOnEmptyQuery: true,
  });

  const boxSizeRef = useRef<HTMLDivElement>(null);
  const { width: inputWidth } = useBoxSize({ ref: boxSizeRef });
  const { left: inputLeft, ref: boxPositionRef } = useBoxPosition();

  const getDisplayValue = (item: TValue | null | undefined): string => {
    if (!item) return '';
    return String((item as Record<string, unknown>)?.[displayField] ?? '');
  };

  const defaultBy = (a: unknown, z: unknown): boolean => {
    if (a === null || z === null || a === undefined || z === undefined) return a === z;
    return JSON.stringify(a) === JSON.stringify(z);
  };

  // Headless UI onChange can pass: TValue | TValue[] | null | null[] | EnsureArray<TValue>
  // We accept unknown and filter out null/empty values at runtime
  const handleChange = (newValue: unknown) => {
    if (newValue === null || newValue === undefined) return;
    if (Array.isArray(newValue) && newValue.every((v) => v === null)) return;
    onChange?.(newValue as TValue & TValue[]);
  };

  const handleRemove = (itemToRemove: TValue) => {
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter((item) => {
        if (by) {
          return item[by] !== itemToRemove[by];
        }
        return JSON.stringify(item) !== JSON.stringify(itemToRemove);
      });
      onChange?.(newValue as TValue & TValue[]);
    }
  };

  const getOptionState = (
    selected: boolean,
    active: boolean
  ): VariantProps<typeof optionVariants>['state'] => {
    if (selected) return 'selected';
    if (active) return 'active';
    return 'default';
  };

  return (
    <div className={cn('flex flex-col', classNames?.root)} {...rootProps}>
      <Combobox
        multiple={multiple}
        value={value}
        onChange={handleChange}
        defaultValue={defaultValue}
        disabled={disabled}
        name={name}
        form={form}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        by={(by ?? defaultBy) as any}
        {...(headlessProps as Record<string, unknown>)}
      >
        {({ open, value: selectedValue }) => {
          return (
            <>
              {label && <Label className="input-label">{label}</Label>}
              <div
                ref={mergeRefs(boxPositionRef, boxSizeRef)}
                className={cn('relative', label && 'mt-1.5')}
              >
                {multiple ? (
                  <div>
                    <ComboboxButton
                      as="div"
                      className={cn(
                        'group',
                        multipleContainerVariants({
                          state: error ? 'error' : 'default',
                        })
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-start gap-1.5 px-3 py-2 pe-9">
                        {Array.isArray(selectedValue) &&
                          selectedValue.length > 0 &&
                          (renderSelected ? (
                            renderSelected(selectedValue as TValue[], handleRemove)
                          ) : (
                            <div>
                              {selectedValue
                                .map((val) => getDisplayValue(val as TValue))
                                .join(', ')}
                            </div>
                          ))}
                        <ComboboxInput
                          as={Input}
                          classNames={{
                            root: 'flex-1',
                            input:
                              'h-auto border-0 px-0 py-0 focus:ring-0 placeholder:font-light placeholder:text-fg-muted',
                          }}
                          displayValue={() => ''}
                          autoComplete="new"
                          placeholder={
                            (!Array.isArray(selectedValue) || selectedValue.length === 0) &&
                            query === ''
                              ? placeholder
                              : undefined
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setQuery(event.target.value);
                          }}
                          value={query}
                          {...inputProps}
                        />
                      </div>

                      <div className="absolute inset-y-0 flex items-center end-0 pe-2">
                        <Icon
                          name="chevron-down"
                          className={cn(
                            'transition-[color,transform]',
                            open
                              ? 'rotate-180 text-primary'
                              : 'text-line-focus group-focus-within:text-primary'
                          )}
                        />
                      </div>
                    </ComboboxButton>
                    <InputErrorMsg when={Boolean(error && typeof error !== 'boolean')}>
                      {error}
                    </InputErrorMsg>
                  </div>
                ) : (
                  <ComboboxButton className="relative w-full cursor-pointer overflow-hidden text-start">
                    <ComboboxInput
                      as={Input}
                      autoComplete="new"
                      error={error}
                      className="cursor-pointer"
                      displayValue={(val: TValue) => getDisplayValue(val)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setQuery(event.target.value)
                      }
                      placeholder={placeholder}
                      prefix={renderInputPrefix?.(selectedValue as TValue | null)}
                      suffix={
                        <Icon
                          name="chevron-down"
                          className={cn('transition-transform', open && 'rotate-180')}
                        />
                      }
                      {...inputProps}
                    />
                  </ComboboxButton>
                )}

                <Transition
                  as={Fragment}
                  enter="transition ease-out"
                  enterFrom="opacity-0 translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-2"
                  afterLeave={() => setQuery('')}
                >
                  <ComboboxOptions
                    anchor={{ to: 'bottom end', gap: 8 }}
                    style={{
                      width: inputWidth,
                      ['--left-anchor' as string]: `${inputLeft}px`,
                    }}
                    className={cn(dropdownVariants(), multiple && 'mt-2')}
                  >
                    {filteredData.length === 0 && query !== '' ? (
                      <div className="relative cursor-default px-4 py-2 text-fg-muted select-none">
                        Nothing found for {query}
                      </div>
                    ) : (
                      filteredData.map(({ item, refIndex }) => (
                        <ComboboxOption
                          key={refIndex}
                          className={({
                            selected,
                            active,
                          }: {
                            selected: boolean;
                            active: boolean;
                          }) =>
                            cn(
                              optionVariants({ state: getOptionState(selected, active) }),
                              renderOption && 'px-3' // Tighter padding for custom renders
                            )
                          }
                          value={item}
                        >
                          {({ selected, focus }: { selected: boolean; focus: boolean }) => (
                            <>
                              {renderOption ? (
                                renderOption(item as TValue, { selected, active: focus })
                              ) : (
                                <span
                                  className={cn(
                                    'block truncate',
                                    selected ? 'font-medium' : 'font-normal'
                                  )}
                                >
                                  {highlight ? (
                                    <Highlight query={query}>
                                      {String(item?.[displayField as keyof DataItem] ?? '')}
                                    </Highlight>
                                  ) : (
                                    String(item?.[displayField as keyof DataItem] ?? '')
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))
                    )}
                  </ComboboxOptions>
                </Transition>
              </div>
            </>
          );
        }}
      </Combobox>
    </div>
  );
}

CustomCombobox.displayName = 'Combobox';

export { CustomCombobox as AutoComplete };
